import {
  Badge,
  BadgeVariantEnum,
  Button,
  ButtonColorEnum,
  ButtonSizeEnum,
  Checkbox,
  CheckThinyIcon,
  IconSizeEnum,
  CloseIcon,
  Tooltip,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PresenceGroupActionsBar } from '@/components/PresenceGroupActionsBar/PresenceGroupActionsBar';
import { getOffsetMs, SpritesheetThumbnail, TSegmentThumbnail } from '@/features/video-thumbnails';
import { PresenceTypeEnum } from '@/types/presences';
import { collectGroupLeafIds, collectGroupLeafItems, resolveItemId } from '@/utils/presence-groups';

export type PresenceClipItem = {
  id?: string;
  tempId?: string;
  image?: string;
  entity_label?: string;
  parameters?: { bbox?: any; segment_id?: string };
  start_datetime?: string;
  end_datetime?: string;
  s_datetime?: Date;
  e_datetime?: Date;
  review_state?: number;
};

export type SegmentThumbnailEntry = {
  thumbnails: TSegmentThumbnail[];
  startDatetime: string;
};

export type SegmentThumbnailMap = Record<string, SegmentThumbnailEntry>;
// Keyed by item ID (tempId ?? id). Built by callers that know segment → item mapping.
export type ItemThumbnailMap = Record<string, SegmentThumbnailEntry>;

type PresenceGroupCardProps = {
  group: PresenceClipItem | { id: any; children: PresenceClipItem[] };
  thumbnailSize: number;
  selectedIds: string[];
  activeItemId?: string;
  onToggleGroupSelection: (ids: string[]) => void;
  onToggleItemSelection: (id: string) => void;
  onPreview: (item: any) => void;
  onValidateGroup: (leafIds: string[], group: PresenceGroupCardProps['group']) => void;
  onInvalidateGroup: (leafIds: string[], group: PresenceGroupCardProps['group']) => void;
  onMove: (targetEntityId: string, leafIds: string[]) => void;
  onDelete: (leafIds: string[]) => void;
  itemThumbnailMap: ItemThumbnailMap;
  presenceType: PresenceTypeEnum;
  isCollapsed?: boolean;
  isOutsideCut?: boolean;
};

export function PresenceGroupCard({
  group,
  thumbnailSize,
  selectedIds,
  activeItemId,
  onToggleGroupSelection,
  onToggleItemSelection,
  onPreview,
  onValidateGroup,
  onInvalidateGroup,
  onMove,
  onDelete,
  itemThumbnailMap,
  presenceType,
  isCollapsed = true,
  isOutsideCut = false,
}: PresenceGroupCardProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(isCollapsed);

  useEffect(() => {
    setCollapsed(isCollapsed);
  }, [isCollapsed]);

  const leafIds = collectGroupLeafIds(group);
  const leafItems: PresenceClipItem[] = collectGroupLeafItems<PresenceClipItem>(group);

  const headerSrc = leafItems[0];
  const headerImageUrl = headerSrc?.image ?? '/default-logo.svg';
  const headerTitle = headerSrc?.entity_label ?? '';

  const allSelected = leafIds.length > 0 && leafIds.every(id => selectedIds.includes(id));
  const someSelected = leafIds.some(id => selectedIds.includes(id));
  const groupSelectedIds = leafIds.filter(id => selectedIds.includes(id));
  const hasGroupSelection = groupSelectedIds.length > 0;
  const validatedGroupItems = leafItems.filter(item => item.review_state === 1);

  const groupSelectedIdsRef = useRef<string[]>([]);
  groupSelectedIdsRef.current = groupSelectedIds;

  const handleToggleCollapsed = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);

  const onDefaultValidateGroup = useCallback(() => {
    onValidateGroup(leafIds, group);
    setCollapsed(true);
  }, [onValidateGroup, leafIds, group]);

  const onDefaultInvalidateGroup = useCallback(() => {
    onInvalidateGroup(leafIds, group);
    // The state is not changed for the 'expand/collapse' behavior.
  }, [onInvalidateGroup, leafIds, group]);

  const handleActionsBarDelete = useCallback(() => {
    onDelete(groupSelectedIdsRef.current);
  }, [onDelete]);

  const handleActionsBarMove = useCallback(
    (targetId: string) => {
      onMove(targetId, groupSelectedIdsRef.current);
    },
    [onMove]
  );

  const handleActionsBarValidate = useCallback(() => {
    onValidateGroup(groupSelectedIdsRef.current, group);
  }, [onValidateGroup, group]);

  const handleActionsBarInvalidate = useCallback(() => {
    onInvalidateGroup(groupSelectedIdsRef.current, group);
  }, [onInvalidateGroup, group]);

  // Only one validated item means that the entire group has been validated.
  const isGroupValidated = leafItems.some(item => item.review_state === 1);
  const allGroupItemsValidated = leafItems.every(item => item.review_state === 1);

  const groupSelectedItems = leafItems.filter(item =>
    groupSelectedIds.includes(resolveItemId(item))
  );
  const isSelectionValidated =
    groupSelectedItems.length > 0 && groupSelectedItems.every(item => item.review_state === 1);

  return (
    <div className="overflow-hidden rounded border border-neutral-20" data-id="presence-group-card">
      <div
        data-id="presence-group-card-header"
        className={clsx(
          'flex items-center gap-2 border-b border-neutral-20 px-2 py-1',
          // Exactly one background class: stacking them would leave the winner to
          // the order of the generated CSS rather than to this decision.
          isOutsideCut
            ? 'bg-feedback-error-10'
            : isGroupValidated
              ? allGroupItemsValidated
                ? 'bg-success-light/50'
                : 'bg-warning-light/50'
              : 'bg-neutral-15'
        )}
      >
        <Checkbox
          checked={allSelected || someSelected}
          indeterminate={someSelected && !allSelected}
          onChange={() => onToggleGroupSelection(leafIds)}
        />
        <img src={headerImageUrl} alt="" className="h-8 w-16 rounded object-contain" />
        <span
          data-id="presence-group-title"
          className="flex-1 cursor-pointer truncate text-sm font-medium"
          onClick={handleToggleCollapsed}
        >
          {headerTitle}
        </span>
        {hasGroupSelection && (
          <PresenceGroupActionsBar
            presenceType={presenceType}
            onMove={handleActionsBarMove}
            onValidate={!isSelectionValidated ? handleActionsBarValidate : undefined}
            onInvalidate={isSelectionValidated ? handleActionsBarInvalidate : undefined}
            onDelete={handleActionsBarDelete}
          />
        )}
        <Tooltip help={t('Presencias validadas / totales')} grow>
          <Badge
            variant={BadgeVariantEnum.DefaultFilled}
          >{`${validatedGroupItems.length} / ${leafIds.length}`}</Badge>
        </Tooltip>
        {isGroupValidated ? (
          <Button
            color={ButtonColorEnum.ERROR}
            className="!bg-feedback-error-10 !text-black"
            size={ButtonSizeEnum.XS}
            rightIcon={() => <CloseIcon size={IconSizeEnum.XS} />}
            onClick={onDefaultInvalidateGroup}
          >
            {t('Invalidar grupo')}
          </Button>
        ) : (
          <Button
            color={ButtonColorEnum.SUCCESS}
            size={ButtonSizeEnum.XS}
            rightIcon={() => <CheckThinyIcon size={IconSizeEnum.XS} />}
            onClick={onDefaultValidateGroup}
          >
            {t('Validar grupo')}
          </Button>
        )}
      </div>
      <div
        data-id="presence-group-card-body"
        className={clsx(
          'flex flex-wrap gap-2 p-2',
          collapsed && 'hidden',
          isOutsideCut
            ? 'bg-feedback-error-20/10'
            : isGroupValidated &&
                (allGroupItemsValidated ? 'bg-success-light/30' : 'bg-warning-light/30')
        )}
      >
        {leafItems.map(item => {
          const itemId = resolveItemId(item);
          const isActive = activeItemId === itemId;
          const isSelected = selectedIds.includes(itemId);
          const startTs = item.s_datetime?.toISOString() ?? item.start_datetime;
          const endTs = item.e_datetime?.toISOString() ?? item.end_datetime;
          const isValidated = item.review_state === 1;
          const segmentData = itemThumbnailMap[itemId];

          return (
            <div key={itemId || item.entity_label} className="group relative">
              <SpritesheetThumbnail
                startOffsetMs={getOffsetMs(startTs, segmentData?.startDatetime ?? null)}
                endOffsetMs={getOffsetMs(endTs, segmentData?.startDatetime ?? null)}
                thumbnails={segmentData?.thumbnails ?? []}
                bbox={item.parameters?.bbox}
                displayWidth={thumbnailSize}
                alt={item.entity_label ?? ''}
                presenceImageUrl={item.image}
                className={clsx(
                  'cursor-pointer',
                  (isActive || isSelected) && 'ring-[3px] ring-blue-500',
                  isValidated && 'border-4 border-feedback-success-20'
                )}
                onClick={() => onPreview(item)}
              />
              <div
                className={clsx(
                  'absolute left-1 top-1',
                  isSelected ? 'flex' : 'hidden group-hover:flex'
                )}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => {
                    onToggleItemSelection(itemId);
                    onPreview(item);
                  }}
                />
              </div>
              {/* An empty id would match every other item without one in onDelete. */}
              {itemId && (
                <div className="absolute right-1 top-1 hidden group-hover:flex">
                  <Tooltip help={t('Eliminar presencia')} grow>
                    <span className="flex rounded-full bg-white/90 p-0.5">
                      <CloseIcon
                        data-id="presence-delete-item"
                        size={IconSizeEnum.SM}
                        className="cursor-pointer"
                        onClick={event => {
                          event.stopPropagation();
                          onDelete([itemId]);
                        }}
                      />
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
